import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface RollTextProps {
    text: string
    className?: string
}

// Hover effect for links and buttons: the label rolls up and a copy rolls in.
// Needs a `group` class on the interactive parent.
export const RollText: FC<RollTextProps> = ({ text, className }): ReactNode => {
    return (
        <span className={cn('relative inline-flex overflow-hidden', className)}>
            <span className='block transition-transform duration-500 ease-out-expo group-hover:-translate-y-full group-focus-visible:-translate-y-full'>
                {text}
            </span>

            <span
                aria-hidden='true'
                className='absolute inset-0 block translate-y-full transition-transform duration-500 ease-out-expo group-hover:translate-y-0 group-focus-visible:translate-y-0'
            >
                {text}
            </span>
        </span>
    )
}

interface RollCharsProps {
    text: string
    className?: string
    stagger?: number
}

// Character by character roll for large type, such as the contact email.
export const RollChars: FC<RollCharsProps> = ({ text, className, stagger = 16 }): ReactNode => {
    return (
        <span aria-hidden='true' className={cn('inline-flex', className)}>
            {Array.from(text).map((char, index) => (
                <span key={`${char}-${index}`} className='relative inline-block overflow-hidden'>
                    <span
                        className='block transition-transform duration-700 ease-out-expo group-hover:-translate-y-full group-focus-visible:-translate-y-full'
                        style={{ transitionDelay: `${index * stagger}ms` }}
                    >
                        {char}
                    </span>

                    <span
                        className='absolute inset-0 block translate-y-full text-tone-accent transition-transform duration-700 ease-out-expo group-hover:translate-y-0 group-focus-visible:translate-y-0'
                        style={{ transitionDelay: `${index * stagger}ms` }}
                    >
                        {char}
                    </span>
                </span>
            ))}
        </span>
    )
}
